// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, DetailModel, ProductInventoryDto, ProductModel, ProductPriceDto, UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import { LoadingSpinner } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import get from "@insite/mobius/utilities/get";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rugby/App/Store/Reducers";
import { getProductAdditionalInfos } from "@rugby/App/Store/Selectors/CustomProductDetails";
import CartIcon from "@rugby/XDL/Icons/CartIcon";
import PlusRound from "@rugby/XDL/Icons/PlusRound";
import React, { ChangeEvent, NamedExoticComponent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

interface LaminateInventory {
    ErpNumber?: string;
    LeadTime?: string;
    UnitofMeasure?: string;
    Length?: number;
    Width?: number;
    Qty?: number;
    WilsonartItem?: string;
}

interface CustomProductModel extends ProductModel {
    laminateInventory?: LaminateInventory;
    pricing?: ProductPriceDto;
    inventory?: ProductInventoryDto;
    detail?: DetailModel;
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct & HasParentProductId) => {
    const settingsCollection = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    const showAddToCartConfirmationDialog = settingsCollection.productSettings.showAddToCartConfirmationDialog;
    const enableTableMode = (settingsCollection?.customSettings as any)?.enableTableMode;
    const variantChildrenData: CustomProductModel[] = getVariantChildrenDataView(state, ownProps.parentProductId).value || [];
    const laminateInventory: LaminateInventory[] = JSON.parse(getProductAdditionalInfos(state)?.laminateInventory || "[]");
    const variantChildren: CustomProductModel[] = variantChildrenData?.map((variant) => {
        return {
            ...variant,
            laminateInventory: laminateInventory.find(wilson => wilson.ErpNumber === variant.productNumber),
        };
    }).sort((a, b) => (a?.detail?.shippingWidth || 0) - (b?.detail?.shippingWidth || 0) || (a?.detail?.shippingLength || 0) - (b?.detail?.shippingLength || 0));

    return {
        enableTableMode,
        variantChildren,
        leadTime: laminateInventory.find(wilson => wilson.LeadTime)?.LeadTime || false,
        showAddToCartConfirmationDialog,
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
    };
};

const mapDispatchToProps = {
    addCartLines: makeHandlerChainAwaitable<Parameters<typeof addCartLines>[0], CartLineCollectionModel>(addCartLines),
    loadCurrentCart,
    setAddToListModalIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct & HasParentProductId;

interface ProductInfoTableStyles {
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableCell?: DataTableCellProps;
    productQuantityOrderedStyles?: TextFieldPresentationProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addSelectedToWishlistLink?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
}

const productInfoTableStyles: ProductInfoTableStyles = {
    dataTableHeader: {
        css: css`
            text-align: center;
        `,
    },
    dataTableCell: {
        css: css`
            padding: 0 12px;
            text-align: center;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
                width: 50px;
            `,
            inputSelect: css`
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -moz-appearance: textfield;
            `,
        },
    },
    productAddToCartButtonStyles: {
        css: css`
            padding: ${(props: any) => get(buttonSizeVariants, [props._sizeVariant, "padding"]) || "0 1em"};
            display: flex;
            justify-content: center;
            align-items: center;
            margin-right: 20px;
            &.button-wrapper-icon-only {
                height: 38px;
                width: 65px;
                padding: 4px 0;
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    addSelectedToWishlistLink: {
        color: "secondary",
        icon: {
            iconProps: {
                src: PlusRound,
                color: "secondary",
                size: 12,
            },
        },
        typographyProps: {
            size: 14,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        css: css`
            &:disabled {
                opacity: .3;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            margin: 20px 0;
            display: flex;
        `,
    },
};

interface AddToCartStatusInfoInterface {
    label: string;
    icon: NamedExoticComponent<{}> | string;
}

interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
    error: AddToCartStatusInfoInterface;
}

const addToCartStatus: AddToCartStatusInterface = {
    default: {
        label: "Add to Cart",
        icon: CartIcon,
    },
    loading: {
        label: "Adding",
        icon: "",
    },
    success: {
        label: "Success",
        icon: Check,
    },
    error: {
        label: "Failed",
        icon: X,
    },
};


const styles = productInfoTableStyles;

const ProductInfoTable = ({
    enableTableMode,
    variantChildren,
    leadTime,
    showAddToCartConfirmationDialog,
    isAddToCartButtonDisabled,
    addCartLines,
    loadCurrentCart,
    setAddToListModalIsOpen,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [order, setOrder] = useState({} as any);
    const [status, setStatus] = useState(addToCartStatus.default);

    useEffect(() => {
        if (variantChildren.length === 1) {
            setOrder({
                [variantChildren[0].id]: 1,
            });
        }
    }, []);

    useEffect(() => {
        if (status === addToCartStatus.success || status === addToCartStatus.error) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    if (!enableTableMode || !variantChildren?.length) {
        return null
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>, id: string) => {
        const value = event.target.value.length > 1 && event.target.value[0] === "0" ? event.target.value.slice(1) : event.target.value;
        setOrder({
            ...order,
            [id]: value,
        });
    };

    const addOrderToCart = async () => {
        const productInfos = (variantChildren as any).reduce((parameter: ProductInfo[], variant: ProductModel) => {
            if (!order[variant.id]) {
                return parameter
            }

            parameter.push({
                productId: variant.id,
                qtyOrdered: order[variant.id],
                unitOfMeasure: variant?.unitOfMeasures?.[0].unitOfMeasure || "",
            } as ProductInfo);

            return parameter;
        }, []);

        setStatus(addToCartStatus.loading);
        const cartLinesCollection = (await addCartLines({ productInfos })) as CartLineCollectionModel;

        if (cartLinesCollection?.cartLines?.length) {
            setStatus(addToCartStatus.success);
            if (showAddToCartConfirmationDialog) {
                const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                    messageType: "success",
                });
            }
        } else {
            setStatus(addToCartStatus.error);
        }

        loadCurrentCart({ shouldLoadFullCart: true });
    };

    const addSelectedToWishlist = () => {
        const productInfos = (variantChildren as any).reduce((parameter: ProductInfo[], variant: ProductModel) => {
            if (!order[variant.id]) {
                return parameter
            }

            parameter.push({
                productId: variant.id,
                qtyOrdered: order[variant.id],
                unitOfMeasure: variant?.unitOfMeasures?.[0].unitOfMeasure || "",
            } as ProductInfo);

            return parameter;
        }, []);
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    const isDisabled =
        status !== addToCartStatus.default ||
        isAddToCartButtonDisabled ||
        variantChildren.every(variant => !variant.pricing) ||
        variantChildren.every(variant => !variant.inventory);

    return (
        <>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("QTY")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("Price")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("UOM")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("Width")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("Length")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("Available Immediately")}</DataTableHeader>
                    {leadTime && typeof leadTime === "string" && (
                        <DataTableHeader {...styles.dataTableHeader} tight>{translate("Available {0} days").replace("{0}", leadTime)}</DataTableHeader>
                    )}
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("Price")}</DataTableHeader>
                    <DataTableHeader {...styles.dataTableHeader} tight>{translate("UOM")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {variantChildren.map(({
                        id,
                        unitOfMeasures,
                        pricing,
                        inventory,
                        laminateInventory,
                        detail,
                    }) => (
                        <DataTableRow key={id}>
                            <DataTableCell {...styles.dataTableCell}>
                                <TextField
                                    value={order[id] || 0}
                                    type="number"
                                    min={0}
                                    max={999999}
                                    data-test-selector="product_qtyOrdered"
                                    disabled={!pricing || pricing.unitNetPrice === 0}
                                    onKeyPress={keyPressHandler}
                                    onChange={(event) => qtyChangeHandler(event, id)}
                                    {...styles.productQuantityOrderedStyles}
                                />
                            </DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{pricing?.unitNetPriceDisplay ? pricing?.unitNetPriceDisplay : translate("Price Not Available")}</DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{unitOfMeasures?.find((uom: UnitOfMeasureModel) => uom.isDefault)?.unitOfMeasureDisplay}</DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{detail?.shippingWidth}</DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{detail?.shippingLength}</DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{inventory ? inventory.qtyOnHand : translate("Inventory Not Available")}</DataTableCell>
                            {leadTime && typeof leadTime === "string" && (
                                <DataTableCell {...styles.dataTableCell}>{laminateInventory?.Qty || 0}</DataTableCell>
                            )}
                            <DataTableCell {...styles.dataTableCell}>{pricing?.additionalResults?.priceUOMNetPrice ? pricing?.additionalResults?.priceUOMNetPrice : translate("Price Not Available")}</DataTableCell>
                            <DataTableCell {...styles.dataTableCell}>{pricing?.additionalResults?.priceUOM}</DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
            <StyledWrapper {...styles.actionsWrapper}>
                <Button
                    icon={{ src: status.icon, position: "left" }}
                    {...styles.productAddToCartButtonStyles}
                    onClick={addOrderToCart}
                    disabled={isDisabled || !Object.keys(order).some(key => order[key] && order[key] !== "0")}
                >
                    {status === addToCartStatus.loading ? (
                        <LoadingSpinner
                            size={15}
                            color="text.main"
                            css={css`
                                margin-right: 8px;
                                margin-top: -1px;
                            `}
                        />
                    ) : null}
                    {translate(status.label ?? "")}
                </Button>
                <Link
                    {...styles.addSelectedToWishlistLink}
                    onClick={addSelectedToWishlist}
                    disabled={!Object.keys(order).some(key => order[key] && order[key] !== "0")}
                >
                    {translate("Add Selected to List")}
                </Link>
            </StyledWrapper>
        </>
    )
};

const widgetModule: WidgetModule = {
    component: withProduct(withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductInfoTable))),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Product Info Table",
    },
};

export default widgetModule;
